import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import type {
	PbjAuditEmployeeRow,
	PbjAuditWorkedSource,
	PbjBreaksRemovedRow,
	PbjDailyHoursCapReportResponse,
	PbjExcludedEmployeeRow,
	PbjExcludeEmployeeBody,
	PbjExclusionImportPreview,
	PbjHoursByRoleDaily,
	PbjLongShiftReviewResponse,
	PbjLongShiftRow,
	PbjMissingPunchRow,
	PbjShiftManualEditBody,
	PbjShortShiftReviewResponse,
	PbjStaffingBulkEditResult,
	PbjStaffingBulkEditRowKey,
	PbjStaffingBulkEdits,
	PbjStaffingColumnVisibility,
	PbjStaffingHoursRow,
	PbjStaffingHoursSummary,
	PbjStaffingShiftRow,
	PbjTerminatedEmployeeRow,
	PbjUpcomingTurnoverRow,
} from "../../../utils/models";
import { PBJ_MODEL_QUERY_KEY, UPCOMING_TURNOVER_QUERY_KEY } from "./usePbjModel";

const rangeOk = (startDate: string, endDate: string) => Boolean(startDate) && Boolean(endDate) && startDate <= endDate;

// DAT-209 pbj_shift-backed Staffing Hours — replaces the nurse-keyed staffing-hours view in the Audit tab.
export const usePbjStaffingHours = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjStaffingHoursRow[]>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "staffing-hours-pbj", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjStaffingHoursPbj(startDate, endDate, departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

/**
 * Group-by summary of the Staffing Hours rows (per-job totals, grand total, weekend/weekday split,
 * agency hours) — the on-screen equivalent of the XLSX summary block. Keyed under the same
 * "staffing-hours-pbj" prefix as the grid so a cell edit invalidates both together.
 */
export const usePbjStaffingHoursSummary = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjStaffingHoursSummary>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "staffing-hours-pbj", "summary", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjStaffingHoursSummary(startDate, endDate, departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

/**
 * Whether to hide the source-specific Staffing Hours columns (EarnCode / Dist Job Code) — true when the
 * dept's shifts source is exclusively Empeon, where those fields are structurally always NULL. The server
 * owns the rule; this hook just reads the bool. While loading, the consumer defaults to showing columns.
 */
export const usePbjStaffingColumnVisibility = () => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjStaffingColumnVisibility>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "staffing-hours-pbj", "column-visibility", departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjStaffingColumnVisibility(departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

export const usePbjAuditEmployees = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjAuditEmployeeRow[]>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "employees", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjAuditEmployees(startDate, endDate, departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

/** Pulls the UIVisibleError translation key off an axios error, when the server sent one. */
const serverErrorTranslationKey = (error: Error): string | null => {
	const detail = (error as AxiosError<{ detail?: { error_type?: string; message?: string } | string }>).response?.data?.detail;
	if (detail && typeof detail === "object" && detail.error_type === "UIVisibleError" && detail.message) return detail.message;
	return null;
};

/**
 * DAT-501 set/clear the manual PBJ Employee ID override for one roster row, keyed by the RAW synced
 * pbj_id (null override reverts to the synced id). The override re-keys empIds across every audit
 * sheet and the CMS export, so success invalidates the whole "audit" query family — same breadth
 * rationale as useEditPbjStaffingShift, but wider than staffing-hours-pbj alone.
 */
export const useEditPbjEmployeeOverrideId = () => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation<void, Error, { pbjEmployeeId: string; pbjOverrideEmployeeId: string | null }>({
		mutationFn: async ({ pbjEmployeeId, pbjOverrideEmployeeId }) => {
			await backendService.patchPbjEmployeeOverrideId({ departmentId, pbjEmployeeId, pbjOverrideEmployeeId });
		},
		onSuccess: (_data, { pbjEmployeeId, pbjOverrideEmployeeId }) => {
			queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "audit"] });
			showSnackbar(t("pbj_model.audit.employees.override.saved"), "success");
			track("pbj_employee_override_id_edited", {
				department_id: departmentId,
				pbj_employee_id: pbjEmployeeId,
				cleared: pbjOverrideEmployeeId === null,
			});
		},
		onError: (error, { pbjEmployeeId }) => {
			// DEFAULT: a server-sent UIVisibleError key (e.g. override.not_found) wins; the generic
			// update_failed copy is the deliberate fallback for non-UIVisible failures (network, 500).
			showSnackbar(t(serverErrorTranslationKey(error) ?? "pbj_model.audit.employees.override.update_failed"), "error");
			trackError("pbj_employee_override_id_edit_error", {
				department_id: departmentId,
				pbj_employee_id: pbjEmployeeId,
				error_message: error?.message,
			});
		},
	});
};

/**
 * Set/clear the manual Pay Type Code override for one roster row, keyed by the RAW synced pbj_id
 * (null payTypeCode reverts to the synced code). Same invalidate breadth as the id-override hook
 * (the whole "audit" query family), since the code re-keys W2/1099 attribution across every sheet.
 */
export const useEditPbjEmployeePayType = () => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation<void, Error, { pbjEmployeeId: string; payTypeCode: "1" | "2" | "3" | null }>({
		mutationFn: async ({ pbjEmployeeId, payTypeCode }) => {
			await backendService.patchPbjEmployeePayType({ departmentId, pbjEmployeeId, payTypeCode });
		},
		onSuccess: (_data, { pbjEmployeeId, payTypeCode }) => {
			queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "audit"] });
			showSnackbar(t("pbj_model.audit.employees.pay_type_override.saved"), "success");
			track("pbj_employee_pay_type_override_edited", {
				department_id: departmentId,
				pbj_employee_id: pbjEmployeeId,
				cleared: payTypeCode === null,
			});
		},
		onError: (error, { pbjEmployeeId }) => {
			// DEFAULT: a server-sent UIVisibleError key (e.g. pay_type_override.not_found) wins; the generic
			// update_failed copy is the deliberate fallback for non-UIVisible failures (network, 500).
			showSnackbar(t(serverErrorTranslationKey(error) ?? "pbj_model.audit.employees.pay_type_override.update_failed"), "error");
			trackError("pbj_employee_pay_type_override_edit_error", {
				department_id: departmentId,
				pbj_employee_id: pbjEmployeeId,
				error_message: error?.message,
			});
		},
	});
};

/**
 * Roster of employees flagged "Exclude from PBJ" across the facility group, with each one's dropped
 * hours for the committed quarter/range. Same immutable-range key convention + staleTime:Infinity as
 * the sibling audit sheets; keyed under the "audit" family so an exclude/un-exclude PATCH invalidates it.
 */
export const usePbjExcludedEmployees = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjExcludedEmployeeRow[]>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "excluded-employees", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjExcludedEmployees(departmentId, startDate, endDate);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

/**
 * Set (exclude=true, non-blank reason enforced by the caller before invoking) or clear (exclude=false)
 * the "Exclude employee from PBJ" flag for one roster row, keyed by the RAW synced pbj_id. The flag
 * drops the employee from the CMS filing, so success invalidates the whole "audit" query family (the
 * excluded roster, the Staffing Hours grid, and every derived sheet) — same breadth as the id-override hook.
 */
export const usePbjExcludeEmployee = () => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation<void, Error, PbjExcludeEmployeeBody>({
		mutationFn: async (body) => {
			await backendService.patchPbjEmployeeExclude(body);
		},
		onSuccess: (_data, body) => {
			queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "audit"] });
			showSnackbar(t(body.exclude ? "pbj_model.audit.employees.exclusion.excluded" : "pbj_model.audit.employees.exclusion.unexcluded"), "success");
			track("pbj_employee_exclusion_edited", {
				department_id: departmentId,
				pbj_employee_id: body.pbjEmployeeId,
				excluded: body.exclude,
			});
		},
		onError: (error, body) => {
			// DEFAULT: a server-sent UIVisibleError key (e.g. exclusion.reason_required / not_found) wins;
			// the generic update_failed copy is the deliberate fallback for non-UIVisible failures (network, 500).
			showSnackbar(t(serverErrorTranslationKey(error) ?? "pbj_model.audit.employees.exclusion.update_failed"), "error");
			trackError("pbj_employee_exclusion_edit_error", {
				department_id: departmentId,
				pbj_employee_id: body.pbjEmployeeId,
				error_message: error?.message,
			});
		},
	});
};

/**
 * Bulk-exclusion file import. `dryRun` drives the two-phase flow: preview (true) renders the diff,
 * commit (false) writes. Returns the preview both times (the component reads `blocked`/`committed`).
 * On a committed run success invalidates the whole "audit" family so the excluded roster + grids refresh.
 * No snackbar/analytics here — the dialog owns UX so it can show the preview inline; it tracks the flow.
 */
export const useImportPbjExclusions = () => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();

	return useMutation<PbjExclusionImportPreview, Error, { file: File; dryRun: boolean }>({
		mutationFn: async ({ file, dryRun }) => {
			const res = await backendService.importPbjExclusions(departmentId, file, dryRun);
			return res.data;
		},
		onSuccess: (preview) => {
			if (preview.committed) queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "audit"] });
		},
	});
};

/**
 * DAT-209 lazy fetch of the underlying pbj_shift rows for one Staffing-Hours group. Only enabled
 * once `enabled` is set (the row is expanded), so we don't fetch shifts for collapsed rows.
 */
export const usePbjStaffingShifts = (params: { pbjEmployeeId: string; date: string; pbjJobId: string | null }, enabled: boolean) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjStaffingShiftRow[]>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "staffing-hours-pbj", "shifts", departmentId, params.pbjEmployeeId, params.date, params.pbjJobId],
		queryFn: async () => {
			const res = await backendService.getPbjStaffingShifts({
				departmentId,
				pbjEmployeeId: params.pbjEmployeeId,
				date: params.date,
				pbjJobId: params.pbjJobId,
			});
			return res.data;
		},
		enabled: enabled && Boolean(departmentId) && Boolean(params.pbjEmployeeId) && Boolean(params.date),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

/**
 * DAT-209 PATCH one pbj_shift's editable Staffing-Hours fields. On success invalidates both the
 * grouped staffing-hours-pbj table and any open expand sub-grids so the edit is reflected everywhere.
 */
export const useEditPbjStaffingShift = () => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation<void, Error, { pbjShiftId: string; body: PbjShiftManualEditBody; changedField: string }>({
		mutationFn: async ({ pbjShiftId, body }) => {
			await backendService.patchPbjStaffingShift(pbjShiftId, body);
		},
		onSuccess: (_data, { pbjShiftId, changedField }) => {
			queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "staffing-hours-pbj"] });
			showSnackbar(t("pbj_model.staffing.edit.saved"), "success");
			track("pbj_staffing_hours_cell_edited", { department_id: departmentId, pbj_shift_id: pbjShiftId, field: changedField });
		},
		onError: (error, { changedField }) => {
			showSnackbar(t("pbj_model.staffing.edit.update_failed"), "error");
			trackError("pbj_staffing_hours_cell_edit_error", { department_id: departmentId, field: changedField, error_message: error?.message });
		},
	});
};

/**
 * Bulk PATCH of the editable Staffing-Hours fields across many selected grid rows (group keys — the
 * server resolves each to ALL contributing pbj_shift rows, so aggregated rows are editable too).
 * `edits` uses absent=untouched / null=clear semantics. On success invalidates the whole
 * staffing-hours-pbj query family; a partial match (matchedRows < rows.length) surfaces a warning.
 */
export const useBulkEditPbjStaffingShifts = () => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation<PbjStaffingBulkEditResult, Error, { rows: PbjStaffingBulkEditRowKey[]; edits: PbjStaffingBulkEdits }>({
		mutationFn: async ({ rows, edits }) => {
			const res = await backendService.bulkPatchPbjStaffingShifts({ departmentId, rows, edits });
			return res.data;
		},
		onSuccess: (result, { rows, edits }) => {
			queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "staffing-hours-pbj"] });
			if (result.matchedRows < rows.length) {
				showSnackbar(t("pbj_model.staffing.bulk.partial", { matched: result.matchedRows, total: rows.length }), "warning");
			} else if (result.appliedRows < rows.length) {
				// Overnight-shared shifts were skipped so the edit doesn't leak onto unselected rows.
				showSnackbar(t("pbj_model.staffing.bulk.partial_overnight", { applied: result.appliedRows, total: rows.length }), "warning");
			} else {
				showSnackbar(t("pbj_model.staffing.bulk.saved", { shifts: result.updatedShifts }), "success");
			}
			track("pbj_staffing_hours_bulk_edited", {
				department_id: departmentId,
				rows_count: rows.length,
				matched_rows: result.matchedRows,
				applied_rows: result.appliedRows,
				updated_shifts: result.updatedShifts,
				fields: Object.keys(edits).join(","),
			});
		},
		onError: (error, { rows, edits }) => {
			showSnackbar(t("pbj_model.staffing.bulk.failed"), "error");
			trackError("pbj_staffing_hours_bulk_edit_error", {
				department_id: departmentId,
				rows_count: rows.length,
				fields: Object.keys(edits).join(","),
				error_message: error?.message,
			});
		},
	});
};

// --- Audit sheet hooks (read-only). Same staleTime/refetch policy + rangeOk guard as above. ---

/**
 * Daily total net-of-breaks hours per role for the Audit "Hours by Role" time-series chart. Committed
 * (immutable) date range → `staleTime: Infinity` + same rangeOk guard/key convention as the sibling
 * audit sheets above; the response reconciles with the Role Summary totals.
 */
export const usePbjHoursByRoleDaily = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjHoursByRoleDaily>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "hours-by-role-daily", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjHoursByRoleDaily(startDate, endDate, departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

export const usePbjBreaksRemoved = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjBreaksRemovedRow[]>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "breaks-removed", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjBreaksRemoved(startDate, endDate, departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

export const usePbjTerminated = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjTerminatedEmployeeRow[]>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "terminated", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjTerminated(startDate, endDate, departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

export const usePbjMissingPunches = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjMissingPunchRow[]>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "missing-punches", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjMissingPunches(startDate, endDate, departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

export const usePbjLongShifts = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjLongShiftRow[]>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "long-shifts", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjLongShifts(startDate, endDate, departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

export const usePbjLongShiftReview = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjLongShiftReviewResponse>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "long-shift-review", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjLongShiftReview(startDate, endDate, departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

export const usePbjShortShiftReview = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjShortShiftReviewResponse>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "short-shift-review", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjShortShiftReview(startDate, endDate, departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

/**
 * Every (employee, dept-local day) whose submitted PBJ hours exceeded the CMS daily cap and were trimmed to
 * exactly it in the filed XLSX/XML — the same rows the submission export trims. Surfaced read-only at the
 * bottom of the Long Shifts audit tab. Shares the committed-range `staleTime: Infinity` policy of its siblings.
 */
export const usePbjSubmissionHourCapReport = (startDate: string, endDate: string) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjDailyHoursCapReportResponse>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "submission-hour-cap-report", startDate, endDate, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjSubmissionHourCapReport(startDate, endDate, departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId) && rangeOk(startDate, endDate),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

/**
 * DAT-810 — which table the Audit tab's numbers came from. No date-range params and no `rangeOk`
 * guard: whether a department has a PBJ mirror is a property of its PBJ onboarding, not of the viewed
 * period, so this is fetched once per department and shared by every sub-tab. `staleTime: Infinity`
 * like the sibling audit hooks — a mirror appearing mid-session is a sync-cycle event, not something
 * to poll for.
 */
export const usePbjAuditWorkedSource = () => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjAuditWorkedSource>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "audit", "worked-source", departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjAuditWorkedSource(departmentId);
			return res.data;
		},
		enabled: Boolean(departmentId),
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

/** Smallest meaningful "within N days" turnover filter — shared by the hook's `enabled` guard and the toolbar input. */
export const MIN_TURNOVER_THRESHOLD_DAYS = 1;

/**
 * Unlike the sibling audit queries (immutable committed date ranges, so `staleTime: Infinity` is sound),
 * upcoming turnover is evaluated as-of-today — day counts drift as time passes, so cap staleness at an hour.
 */
const UPCOMING_TURNOVER_STALE_TIME_MS = 60 * 60 * 1000;

/**
 * Employees approaching PBJ turnover, evaluated as-of-today — deliberately no date params and no
 * rangeOk guard (the audit page's quarter doesn't apply). `thresholdDays` is the view-only filter
 * and is part of the key, so the tab badge and the table share one cached fetch per threshold.
 */
export const usePbjUpcomingTurnover = (thresholdDays: number) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	return useQuery<PbjUpcomingTurnoverRow[]>({
		queryKey: [...UPCOMING_TURNOVER_QUERY_KEY, thresholdDays, departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjUpcomingTurnover(departmentId, thresholdDays);
			return res.data;
		},
		enabled: Boolean(departmentId) && thresholdDays >= MIN_TURNOVER_THRESHOLD_DAYS,
		// As-of-today data: finite staleTime + window-focus refetch (defaults) keep long-lived sessions honest.
		staleTime: UPCOMING_TURNOVER_STALE_TIME_MS,
	});
};
